/**
 * Управленческий P&L.
 *
 * Выручка, себестоимость и валовая прибыль — из продаж. Ниже — черновые
 * операционные расходы и EBITDA: оборотов по счетам в «Управлении
 * торговлей» нет, но расходы клиент проводит поступлением услуг
 * («Оплата труда», «Налоги», аренда), и номенклатура услуги служит
 * статьёй. Это помечено как черновик везде, где показано, и рядом
 * стоит сверка с оплатами из ДДС: что оплачено, но поступлением
 * не проведено, в EBITDA не попало — и это видно строкой.
 */

import { useState, type CSSProperties } from 'react'
import { useFinance } from '../context'
import { indexBy, num, rowKey, useSettled } from '../data'
import { BarRows, ColumnChart, TweenText } from '../charts'
import { Caveat, DataNotes, Delta, Panel, Segmented, SkeletonColumns, SkeletonRows } from '../parts'
import { originFrom } from '../Drawer'
import { useCashByItem, useCashByPnlLine, useCashCoverage, usePayroll, usePnlByLine, usePnlRelations, useSales, useSalesSource } from '../queries'
import { compareLabel, labelOf, monthLabel, monthsOf, previousOf, shortMonth } from '../period'
import { gapOf, missingOf } from '../coverage'
import { salesEmpty } from '../frame'
import { DIMENSION_TITLES, SALES_SERIES, money } from './Overview'
import { usePublishFreshness } from '../freshness'
import { useMediaQuery } from '../motion'
import { deltaPercent, deltaPoints, formatMoney, formatMoneyShort, formatPercent, toNumber } from '../../format'
import { RELATION_OWN } from '../settings'

/** Строки операционных расходов в порядке P&L (src/finance/classify.ts). */
const EXPENSE_ORDER = [
  'Оплата труда',
  'Налоги и взносы',
  'Аренда и лизинг',
  'Маркетинг и реклама',
  'Связь, IT и программы',
  'Профессиональные услуги и обучение',
  'Страхование',
  'Ремонт и содержание',
  'Транспорт, доставка и командировки',
  'Банковские комиссии',
  'Прочие операционные расходы',
  'Не размечено',
]
const BELOW_EBITDA = ['Проценты по кредитам', 'Проценты полученные', 'Налог на прибыль']
const NOT_EXPENSE = 'Не расход периода'

interface Line {
  key: string
  label: string
  note?: string
  kind: 'money' | 'percent'
  level: 'main' | 'sub' | 'result'
  invert?: boolean
}

const LINES: Line[] = [
  { key: 'revenue', label: 'Выручка без НДС', kind: 'money', level: 'main' },
  { key: 'revenue_without_cost', label: 'в т. ч. без себестоимости', note: 'Маржа по этим строкам не считается', kind: 'money', level: 'sub', invert: true },
  { key: 'revenue_with_cost', label: 'Выручка с известной себестоимостью', kind: 'money', level: 'main' },
  { key: 'cost', label: 'Себестоимость', kind: 'money', level: 'main', invert: true },
  { key: 'gross_profit', label: 'Валовая прибыль', note: 'Выручка с себестоимостью − себестоимость', kind: 'money', level: 'result' },
  { key: 'margin_percent', label: 'Рентабельность', note: 'Валовая прибыль ÷ выручка с себестоимостью', kind: 'percent', level: 'result' },
]

export function Pnl() {
  const ctx = useFinance()
  const { range, previousRange, trend, selection, branch, branchIgnored, drill, coverage, noCompare, gap, scopeLabel } = ctx
  const source = useSalesSource()
  const [dimension, setDimension] = useState<string>('direction')

  const totalLive = useSales('direction', range)
  const totalBeforeLive = useSales('direction', previousRange)
  const breakdownLive = useSales(dimension, range)
  const breakdownBeforeLive = useSales(dimension, previousRange)
  const monthlyLive = useSales('period', trend)
  const cash = useCashByItem(range)
  const cashCoverage = useCashCoverage()
  const pnlLive = usePnlByLine(range)
  const pnlBeforeLive = usePnlByLine(previousRange)
  const cashLines = useCashByPnlLine(range)
  const pnlRelations = usePnlRelations(range)
  const payroll = usePayroll(range, 'department')
  usePublishFreshness([totalLive, cash])

  // Текущий и прошлый период — одной парой и с подписями того выбора,
  // к которому относятся: пока пара не готова, на экране прежняя целиком.
  const head = useSettled([totalLive, totalBeforeLive], { selection, noCompare, scopeLabel })
  const [total, totalBefore] = head.states
  const split = useSettled([breakdownLive, breakdownBeforeLive], { selection, noCompare, dimension })
  const [breakdown, breakdownBefore] = split.states
  const trendView = useSettled([monthlyLive], { selection, trend, range, coverage })
  const [monthly] = trendView.states

  const incomplete = Boolean(head.view.noCompare)
  const now = total.data?.totals ?? {}
  const before = totalBefore.data?.totals ?? {}
  const months = monthsOf(trendView.view.trend)
  const byMonth = indexBy(monthly.data?.rows, 'period')
  const rangeMonths = new Set(monthsOf(trendView.view.range))
  const shownDimension = split.view.dimension
  const splitIncomplete = Boolean(split.view.noCompare)
  const previousByKey = indexBy(breakdownBefore.data?.rows, shownDimension)
  const rows = [...(breakdown.data?.rows ?? [])].sort((a, b) => num(b.revenue) - num(a.revenue))
  const revenueTotal = num(breakdown.data?.totals.revenue)

  const outflows = [...(cash.data?.rows ?? [])].filter((row) => num(row.outflow) > 0).sort((a, b) => num(b.outflow) - num(a.outflow))

  // Черновой P&L: есть, если отчёт выдан и в нём выданы расходы.
  const draft = useSettled([pnlLive, pnlBeforeLive], { selection, noCompare, scopeLabel })
  const [pnl, pnlBefore] = draft.states
  const hasDraft = pnlLive.status !== 'idle'
  const pnlNow = pnl.data?.totals ?? {}
  const pnlPrev = pnlBefore.data?.totals ?? {}
  const lineOf = (state: typeof pnl, line: string) => state.data?.rows.find((row) => row.pnl_line === line)
  const expenseLines = EXPENSE_ORDER.filter((line) => num(lineOf(pnl, line)?.opex) !== 0 || num(lineOf(pnlBefore, line)?.opex) !== 0)
  const excluded = num(lineOf(pnl, NOT_EXPENSE)?.expense_excluded)
  const ownExpense = num(pnlRelations.data?.rows.find((row) => row.relation === RELATION_OWN)?.opex)
  const ownRevenue = num(pnlRelations.data?.rows.find((row) => row.relation === RELATION_OWN)?.revenue)
  const draftIncomplete = Boolean(draft.view.noCompare)

  // Сверка с оплатами: что проведено поступлением и что ушло деньгами.
  const paidByLine = new Map((cashLines.data?.rows ?? []).map((row) => [String(row.pnl_line), num(row.outflow)]))
  const reconLines = [...EXPENSE_ORDER, ...BELOW_EBITDA].filter((line) => num(lineOf(pnl, line)?.opex) !== 0 || (paidByLine.get(line) ?? 0) !== 0)
  const paidOnly = reconLines.filter((line) => num(lineOf(pnl, line)?.opex) === 0 && (paidByLine.get(line) ?? 0) > 0)
  const paidOnlySum = paidOnly.filter((line) => !BELOW_EBITDA.includes(line)).reduce((sum, line) => sum + (paidByLine.get(line) ?? 0), 0)
  const payrollRows = payroll.data?.rows ?? []
  const payrollTotal = num(payroll.data?.totals.payroll_amount)

  if (!source?.withMargin) {
    return (
      <Panel title="P&L" states={[{ status: 'forbidden', data: null, refreshing: false, message: 'Для P&L нужен отчёт «Маржинальность по филиалам» — он не входит в ваши права.' }]}>
        {() => null}
      </Panel>
    )
  }

  return (
    <div className="fin-stack">
      {ctx.partial && <Caveat tone="warn">{ctx.partial}</Caveat>}
      <div className="fin-grid fin-grid--main">
        <Panel
          index={0}
          title={`Управленческий P&L · ${labelOf(head.view.selection)}`}
          subtitle={`${head.view.scopeLabel} · сравнение с периодом «${labelOf(previousOf(head.view.selection))}»`}
          states={[total, totalBefore]}
          skeleton={<SkeletonRows rows={6} height={16} />}
          empty={salesEmpty(ctx, Boolean(gap))}
        >
          {() => (
            <div className="table-wrap">
              <table className="fin-table pnl-table">
                <thead>
                  <tr>
                    <th>Статья</th>
                    <th className="num">{labelOf(head.view.selection)}</th>
                    <th className="num">{labelOf(previousOf(head.view.selection))}</th>
                    <th className="num">Изменение</th>
                  </tr>
                </thead>
                <tbody>
                  {LINES.map((line, index) => (
                    <tr key={line.key} className={`pnl-line pnl-line--${line.level} cascade`} style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                      <td>
                        <div>{line.label}</div>
                        {line.note && <div className="tiny muted">{line.note}</div>}
                      </td>
                      <td className="num mono">
                        <TweenText
                          memoryKey={`pnl.${line.key}`}
                          value={toNumber(now[line.key])}
                          format={line.kind === 'percent' ? (value) => formatPercent(value) : (value) => formatMoney(value)}
                        />
                      </td>
                      <td className="num mono secondary">
                        {line.kind === 'percent' ? formatPercent(before[line.key]) : formatMoney(before[line.key])}
                      </td>
                      <td className="num">
                        {incomplete ? (
                          <span className="muted tiny">{head.view.noCompare === 'период не закончен — без сравнения' ? 'период идёт' : 'без сравнения'}</span>
                        ) : line.kind === 'percent' ? (
                          <Delta value={deltaPoints(now[line.key], before[line.key])} points />
                        ) : (
                          <Delta value={deltaPercent(now[line.key], before[line.key])} invert={line.invert} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {hasDraft && cashLines.status !== 'idle' ? (
          <Panel
            index={1}
            title="Расходы: проведено и оплачено"
            subtitle="Поступления услуг против выплат по ДДС за период"
            states={[pnl, cashLines]}
            skeleton={<SkeletonRows rows={6} />}
            empty={{ when: reconLines.length === 0, title: 'Расходов за период нет', hint: 'Ни поступлений услуг, ни выплат по статьям P&L за период.' }}
          >
            {() => (
              <>
                {branchIgnored('cashflow') && <Caveat tone="warn">Оплаты — по всей компании: движение денег не делится по филиалам.</Caveat>}
                <div className="table-wrap">
                  <table className="fin-table">
                    <thead>
                      <tr>
                        <th>Строка P&L</th>
                        <th className="num">Поступлением</th>
                        <th className="num">Оплачено</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconLines.map((line, index) => {
                        const accrued = num(lineOf(pnl, line)?.opex)
                        const paid = paidByLine.get(line) ?? 0
                        const below = BELOW_EBITDA.includes(line)
                        return (
                          <tr key={line} className="cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                            <td>
                              <div>{line}</div>
                              {below ? (
                                <div className="tiny muted">ниже EBITDA</div>
                              ) : accrued === 0 && paid > 0 ? (
                                <div className="tiny cell-negative">оплачено без поступления — в EBITDA не попало</div>
                              ) : null}
                            </td>
                            <td className="num mono">{accrued ? formatMoneyShort(accrued) : '—'}</td>
                            <td className="num mono secondary">{paid ? formatMoneyShort(paid) : '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="tiny muted fin-footnote--in-panel">
                  Оплата и начисление расходятся по времени: зарплату за месяц платят в следующем. Строка без
                  поступления — расход, который в 1С проведён только платежом; в черновую EBITDA он не входит.
                </p>
              </>
            )}
          </Panel>
        ) : (
        <Panel
          index={1}
          title="Расходы по оплатам"
          subtitle="Списания по статьям ДДС — по оплатам, не по начислению"
          states={[cash]}
          skeleton={<SkeletonRows rows={5} />}
          empty={salesEmpty(ctx, outflows.length === 0, 'Списаний за период нет', {
            gap: gapOf(selection, range, cashCoverage, 'движения денег'),
            noun: 'списаний',
          })}
        >
          {() => (
            <>
              <Caveat>
                Это деньги, ушедшие со счетов за период, а не затраты периода: оплата поставщику
                за товар уже входит в себестоимость. Поэтому из валовой прибыли эти суммы не вычитаются.
              </Caveat>
              {branchIgnored('cashflow') && <Caveat tone="warn">Вся компания: движение денег не делится по филиалам.</Caveat>}
              <BarRows
                ariaLabel="Списания по статьям ДДС"
                series={[{ key: 'outflow', label: 'Списания', color: 'var(--flow-out)' }]}
                rows={outflows.slice(0, 8).map((row) => ({
                  key: rowKey(row, 'cashflow_item'),
                  label: String(row.cashflow_item ?? '—'),
                  values: [num(row.outflow)],
                }))}
                onSelect={(row, element) => drill({ kind: 'cash_item', value: row.key, label: row.label }, originFrom(element))}
              />
            </>
          )}
        </Panel>
        )}
      </div>

      {hasDraft && (
        <Panel
          index={2}
          title={`Операционные расходы и EBITDA · ${labelOf(draft.view.selection)}`}
          subtitle={`Черновой расчёт по поступлениям услуг · ${draft.view.scopeLabel}`}
          states={[pnl, pnlBefore]}
          skeleton={<SkeletonRows rows={8} height={16} />}
          empty={salesEmpty(ctx, num(pnlNow.revenue) === 0 && num(pnlNow.opex) === 0)}
        >
          {() => (
            <>
              <Caveat>
                Черновой: расходы — это поступления услуг, статья — номенклатура услуги («Оплата труда», «Налоги»,
                «Арендная плата»), разметка правится в админке. Не попадает то, что в 1С проведено не поступлением:
                выплаты из ДДС без документа (см. сверку рядом), авансовые отчёты, амортизация. С бухгалтерией
                не сверяется.
                {ownExpense > 0 && ` В т. ч. услуги от своих организаций — ${money(ownExpense)}${ownRevenue > 0 ? `, продажи своим — ${money(ownRevenue)}` : ''}: в консолидации это внутренние обороты.`}
              </Caveat>
              <div className="table-wrap">
                <table className="fin-table pnl-table pnl-table--wide">
                  <thead>
                    <tr>
                      <th>Статья</th>
                      <th className="num">{labelOf(draft.view.selection)}</th>
                      <th className="num">{labelOf(previousOf(draft.view.selection))}</th>
                      <th className="num">Изменение</th>
                    </tr>
                  </thead>
                  <tbody>
                    <DraftRow label="Выручка без НДС" now={pnlNow.revenue} before={pnlPrev.revenue} incomplete={draftIncomplete} level="main" index={0} />
                    <DraftRow label="Себестоимость" now={pnlNow.cost} before={pnlPrev.cost} incomplete={draftIncomplete} level="main" invert index={1} />
                    <DraftRow
                      label="Валовой доход"
                      note="Выручка − себестоимость, включая услуги без себестоимости"
                      now={num(pnlNow.revenue) - num(pnlNow.cost)}
                      before={pnlBefore.data ? num(pnlPrev.revenue) - num(pnlPrev.cost) : null}
                      incomplete={draftIncomplete}
                      level="result"
                      index={2}
                    />
                    {expenseLines.map((line, index) => (
                      <DraftRow
                        key={line}
                        label={line}
                        note={line === 'Не размечено' ? 'Услуги без статьи — разметьте в админке' : undefined}
                        now={lineOf(pnl, line)?.opex ?? 0}
                        before={lineOf(pnlBefore, line)?.opex ?? 0}
                        incomplete={draftIncomplete}
                        level="expense"
                        invert
                        index={3 + Math.min(index, 4)}
                      />
                    ))}
                    <DraftRow label="Операционные расходы" now={pnlNow.opex} before={pnlPrev.opex} incomplete={draftIncomplete} level="main" invert index={8} />
                    <DraftRow label="EBITDA (черновая)" note="Валовой доход − операционные расходы" now={pnlNow.ebitda} before={pnlPrev.ebitda} incomplete={draftIncomplete} level="result" index={9} />
                    <DraftRow label="EBITDA, % выручки" now={pnlNow.ebitda_margin} before={pnlPrev.ebitda_margin} incomplete={draftIncomplete} level="sub" percent index={10} />
                  </tbody>
                </table>
              </div>
              {excluded > 0 && (
                <p className="tiny muted fin-footnote--in-panel">
                  Услуги, отмеченные «не расход периода» (перепродажа, в себестоимости), — {money(excluded)}: в EBITDA не входят.
                </p>
              )}
            </>
          )}
        </Panel>
      )}

      {payroll.status !== 'idle' && (
        <Panel
          index={3}
          title="ФОТ по подразделениям"
          subtitle={`«Оплата труда» поступлением услуг · ${labelOf(selection)}${payrollTotal > 0 ? ` · всего ${money(payrollTotal)}` : ''}`}
          states={[payroll]}
          skeleton={<SkeletonRows rows={4} />}
          empty={salesEmpty(ctx, payrollRows.length === 0, 'Начислений «Оплата труда» за период нет', { gap: null, noun: 'начислений ФОТ' })}
        >
          {() => (
            <>
              <Caveat>
                Сводный ФОТ из поступлений услуги «Оплата труда»: без сотрудников и численности. Зарплата, выплаченная
                ведомостью или через подотчёт без поступления, сюда не попадает. Полный ФОТ придёт из ЗУП или Excel.
              </Caveat>
              <BarRows
                ariaLabel="ФОТ по подразделениям"
                series={[{ key: 'payroll_amount', label: 'ФОТ', color: 'var(--series-1)' }]}
                rows={payrollRows.slice(0, 10).map((row) => ({
                  key: rowKey(row, 'department'),
                  label: String(row.department ?? '—'),
                  sub: payrollTotal > 0 ? `${formatPercent((num(row.payroll_amount) / payrollTotal) * 100)} от ФОТ` : undefined,
                  values: [num(row.payroll_amount)],
                }))}
              />
            </>
          )}
        </Panel>
      )}

      <Panel
        index={4}
        title="Выручка и валовая прибыль по месяцам"
        subtitle="12 месяцев, которыми заканчивается выбранный период"
        states={[monthly]}
        skeleton={<SkeletonColumns height={220} />}
      >
        {() => (
          <ColumnChart
            ariaLabel="Выручка и валовая прибыль по месяцам"
            height={220}
            series={SALES_SERIES}
            summaryTitle={labelOf(trendView.view.selection)}
            points={months.map((month) => ({
              key: month,
              label: shortMonth(month),
              title: `${monthLabel(month)} · маржа ${formatPercent(byMonth.get(month)?.margin_percent)}`,
              highlight: rangeMonths.has(month),
              missing: missingOf(month, trendView.view.coverage),
              values: [num(byMonth.get(month)?.revenue), num(byMonth.get(month)?.gross_profit)],
            }))}
          />
        )}
      </Panel>

      <Panel
        index={5}
        title="P&L по разрезам"
        subtitle={`Строка открывает детализацию${splitIncomplete ? '' : ` · изменение маржи ${compareLabel(split.view.selection)}`}`}
        states={[breakdown, breakdownBefore]}
        skeleton={<SkeletonRows rows={6} height={16} />}
        empty={salesEmpty(ctx, rows.length === 0)}
        actions={
          <Segmented
            size="sm"
            label="Разрез"
            value={dimension}
            onChange={setDimension}
            options={[
              { value: 'direction', label: DIMENSION_TITLES.direction! },
              { value: 'nomenclature_group', label: DIMENSION_TITLES.nomenclature_group! },
              ...(branch ? [] : [{ value: 'branch', label: DIMENSION_TITLES.branch! }]),
            ]}
          />
        }
      >
        {() => (
          <div className="table-wrap">
            <table className="fin-table fin-table--interactive">
              <thead>
                <tr>
                  <th>{DIMENSION_TITLES[shownDimension]}</th>
                  <th className="num">Выручка</th>
                  <th className="num">Доля</th>
                  <th className="num">Себестоимость</th>
                  <th className="num">Валовая прибыль</th>
                  <th className="num">Маржа</th>
                  <th className="num">Δ маржи</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const key = rowKey(row, shownDimension)
                  const previous = previousByKey.get(key)
                  const label = String(row[shownDimension] ?? '—')
                  return (
                    <tr
                      key={key}
                      className="cascade"
                      style={{ '--i': Math.min(index, 7) } as CSSProperties}
                      tabIndex={0}
                      onClick={(event) => drill({ kind: 'sales', dimension: shownDimension, value: key, label }, originFrom(event.currentTarget))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          drill({ kind: 'sales', dimension: shownDimension, value: key, label }, originFrom(event.currentTarget))
                        }
                      }}
                    >
                      <td className="strong">{label}</td>
                      <td className="num mono">{formatMoneyShort(row.revenue)}</td>
                      <td className="num mono muted">{revenueTotal > 0 ? formatPercent((num(row.revenue) / revenueTotal) * 100) : '—'}</td>
                      <td className="num mono">{formatMoneyShort(row.cost)}</td>
                      <td className={`num mono${num(row.gross_profit) < 0 ? ' cell-negative' : ''}`}>{formatMoneyShort(row.gross_profit)}</td>
                      <td className="num mono">{formatPercent(row.margin_percent)}</td>
                      <td className="num">
                        {splitIncomplete ? <span className="muted">—</span> : <Delta value={deltaPoints(row.margin_percent, previous?.margin_percent)} points />}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <DataNotes states={[total, breakdown, monthly]} />
      <p className="fin-footnote">
        {hasDraft ? (
          <>
            EBITDA здесь черновая: расходы собраны из поступлений услуг, а не из бухгалтерских оборотов.
            {paidOnlySum > 0 && ` За период ${money(paidOnlySum)} оплачено по статьям расходов без поступления — в EBITDA их нет.`}{' '}
            Чистой прибыли нет: амортизации, процентов начислением и налога на прибыль в этих данных нет.
          </>
        ) : (
          <>
            EBITDA и чистой прибыли здесь нет: в «Управлении торговлей» нет оборотов по счетам, а черновой P&L
            по поступлениям услуг вам не выдан. Валовая прибыль {money(num(now.gross_profit))} — последняя строка,
            которую эти данные позволяют посчитать честно.
          </>
        )}
      </p>
    </div>
  )
}

function DraftRow({
  label,
  note,
  now,
  before,
  incomplete,
  level,
  invert,
  percent,
  index,
}: {
  label: string
  note?: string
  now: unknown
  before: unknown
  incomplete: boolean
  level: 'main' | 'sub' | 'result' | 'expense'
  invert?: boolean
  percent?: boolean
  index: number
}) {
  // На телефоне три колонки полных рублей не помещаются: суммы сокращаются.
  const compact = useMediaQuery('(max-width: 600px)')
  const format = percent ? (value: unknown) => formatPercent(value) : (value: unknown) => (compact ? formatMoneyShort(value) : formatMoney(value))
  return (
    <tr className={`pnl-line pnl-line--${level} cascade`} style={{ '--i': Math.min(index, 7) } as CSSProperties}>
      <td>
        <div>{label}</div>
        {note && <div className="tiny muted">{note}</div>}
      </td>
      <td className={`num mono${!percent && level === 'result' && num(now) < 0 ? ' cell-negative' : ''}`}>
        <TweenText memoryKey={`pnl.draft.${label}`} value={toNumber(now)} format={(value) => format(value)} />
      </td>
      <td className="num mono secondary">{format(before)}</td>
      <td className="num">
        {incomplete ? (
          <span className="muted tiny">без сравнения</span>
        ) : percent ? (
          <Delta value={deltaPoints(now, before)} points />
        ) : (
          <Delta value={deltaPercent(now, before)} invert={invert} />
        )}
      </td>
    </tr>
  )
}
